import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Users, TrendingUp, ChevronRight, ArrowLeft } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from "recharts";

export default function RatingsDashboard() {
  const [techRatings, setTechRatings] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [techDetail, setTechDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await API.get("/ratings/summary/all");
      setTechRatings(data.technicians);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchDetail = async (vendorId) => {
    try {
      const { data } = await API.get(`/ratings/technician/${vendorId}`);
      setTechDetail(data);
      setSelectedTech(vendorId);
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const overallAvg = techRatings.length
    ? (techRatings.reduce((s, t) => s + t.average_rating * t.total_ratings, 0) /
       Math.max(1, techRatings.reduce((s, t) => s + t.total_ratings, 0))).toFixed(1)
    : "0.0";
  const totalReviews = techRatings.reduce((s, t) => s + t.total_ratings, 0);

  const chartData = techRatings
    .filter(t => t.total_ratings > 0)
    .map(t => ({ name: t.name.split(" ")[0], rating: t.average_rating, reviews: t.total_ratings }));

  // Detail view
  if (selectedTech && techDetail) {
    const tech = techRatings.find(t => t.vendor_id === selectedTech);
    return (
      <div className="space-y-6" data-testid="ratings-detail">
        <Button variant="outline" size="sm" onClick={() => { setSelectedTech(null); setTechDetail(null); }}
          className="text-xs" data-testid="back-to-ratings">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to All
        </Button>

        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">{tech?.name}</h1>
          <p className="text-sm text-zinc-500">Customer Satisfaction Ratings</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border border-zinc-200">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                <span className="text-2xl font-bold text-zinc-900">{techDetail.summary.average_rating}</span>
              </div>
              <p className="text-[10px] text-zinc-400 uppercase font-bold">Average</p>
            </CardContent>
          </Card>
          <Card className="border border-zinc-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-zinc-900">{techDetail.summary.total_ratings}</p>
              <p className="text-[10px] text-zinc-400 uppercase font-bold">Total Reviews</p>
            </CardContent>
          </Card>
          <Card className="border border-zinc-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {techDetail.summary.total_ratings > 0
                  ? Math.round((techDetail.summary.distribution[5] || 0) / techDetail.summary.total_ratings * 100)
                  : 0}%
              </p>
              <p className="text-[10px] text-zinc-400 uppercase font-bold">5-Star Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Distribution */}
        <Card className="border border-zinc-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-3">Rating Distribution</h3>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(s => {
                const count = techDetail.summary.distribution[s] || 0;
                const pct = techDetail.summary.total_ratings > 0 ? (count / techDetail.summary.total_ratings * 100) : 0;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-zinc-600 w-4">{s}</span>
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    <div className="flex-1 h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-zinc-500 w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Individual Reviews */}
        <div>
          <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-3">Recent Reviews</h3>
          {techDetail.ratings.length > 0 ? (
            <div className="space-y-2">
              {techDetail.ratings.map(r => (
                <Card key={r.id} className="border border-zinc-200" data-testid={`review-${r.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "fill-amber-400 text-amber-400" : "text-zinc-200"}`} />
                        ))}
                      </div>
                      <span className="text-[10px] text-zinc-400">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    {r.comment && <p className="text-xs text-zinc-600">{r.comment}</p>}
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] text-zinc-400">{r.customer_name}</span>
                      <span className="text-[10px] text-zinc-300">-</span>
                      <span className="text-[10px] text-zinc-400">{r.service_type}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-400 text-center py-4">No reviews yet</p>
          )}
        </div>
      </div>
    );
  }

  // Overview
  return (
    <div className="space-y-6" data-testid="ratings-dashboard">
      <div>
        <h1 className="text-2xl font-heading font-bold text-zinc-900">Customer Ratings</h1>
        <p className="text-sm text-zinc-500">Technician satisfaction scores and reviews</p>
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border border-zinc-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              <span className="text-2xl font-bold text-zinc-900">{overallAvg}</span>
            </div>
            <p className="text-[10px] text-zinc-400 uppercase font-bold">Overall Average</p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-zinc-900">{totalReviews}</p>
            <p className="text-[10px] text-zinc-400 uppercase font-bold">Total Reviews</p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-zinc-900">{techRatings.filter(t => t.total_ratings > 0).length}</p>
            <p className="text-[10px] text-zinc-400 uppercase font-bold">Rated Techs</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card className="border border-zinc-200">
          <CardContent className="p-5">
            <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Ratings by Technician</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v, name) => name === "rating" ? `${v}/5` : v} />
                <Bar dataKey="rating" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Avg Rating" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Technician List */}
      <div>
        <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-3">All Technicians</h3>
        <div className="space-y-2">
          {techRatings.map(t => (
            <Card key={t.vendor_id} className="border border-zinc-200 cursor-pointer hover:border-zinc-300 transition-colors"
              onClick={() => fetchDetail(t.vendor_id)} data-testid={`tech-rating-${t.vendor_id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center">
                  <Users className="w-4 h-4 text-zinc-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900">{t.name}</p>
                  <p className="text-[10px] text-zinc-400">{t.total_ratings} review{t.total_ratings !== 1 ? "s" : ""}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {t.total_ratings > 0 ? (
                    <>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(t.average_rating) ? "fill-amber-400 text-amber-400" : "text-zinc-200"}`} />
                        ))}
                      </div>
                      <span className="text-sm font-bold text-zinc-700">{t.average_rating}</span>
                    </>
                  ) : (
                    <span className="text-xs text-zinc-400">No reviews</span>
                  )}
                  <ChevronRight className="w-4 h-4 text-zinc-300" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
